import { FiFeather } from 'react-icons/fi';

export const nuancedExpressionsData = {
  courseId: 'nuanced-expressions',
  courseType: 'vocabulary',
  title: 'Nuanced Expressions',
  icon: FiFeather,
  overview: {
    impact: 'Replace common words with precise, Band 8+ vocabulary',
    focus: 'High-impact academic words with subtle meaning differences',
    uses: 'Writing Task 2 sophistication, Speaking Part 3 precision',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'What Makes Vocabulary "Nuanced"?',
      content: `<p>Nuanced vocabulary refers to words that express <strong>precise shades of meaning</strong> that common words cannot. Instead of saying something is "important", you might say it is <em>pivotal</em> (a turning point), <em>paramount</em> (above all else), or <em>integral</em> (inseparable from the whole). Each word carries a distinct connotation.</p>
<p>IELTS Band 8–9 descriptors explicitly reward "less common lexical items" used with "precision" and "natural" control. The key is not just knowing sophisticated words — it is using them <strong>accurately and appropriately</strong> so they enhance rather than obscure your meaning.</p>`,
      points: [
        'Nuanced words convey specific shades of meaning that common synonyms miss',
        'Band 8+ requires "less common items" used with "precision" — not just big words',
        'A misused sophisticated word is worse than a correctly used simple one',
        'Context determines which nuanced word is appropriate',
      ],
    },
    {
      title: 'High-Impact Words for IELTS',
      content: `<p>The following twelve words are especially useful for IELTS because they appear frequently in academic texts and can replace overused common vocabulary.</p>
<table>
  <thead><tr><th>Word</th><th>Meaning</th><th>Common Equivalent</th><th>Example Sentence</th></tr></thead>
  <tbody>
    <tr><td><strong>pivotal</strong></td><td>of crucial importance; a turning point</td><td>very important</td><td>Education plays a pivotal role in economic development.</td></tr>
    <tr><td><strong>inherent</strong></td><td>existing as a natural/permanent part</td><td>built-in, natural</td><td>There are inherent risks in any investment strategy.</td></tr>
    <tr><td><strong>detrimental</strong></td><td>causing harm or damage</td><td>bad, harmful</td><td>Air pollution is detrimental to public health.</td></tr>
    <tr><td><strong>paramount</strong></td><td>more important than anything else</td><td>most important</td><td>Public safety should be of paramount concern.</td></tr>
    <tr><td><strong>unprecedented</strong></td><td>never done or known before</td><td>never seen before</td><td>The pandemic caused unprecedented disruption to education.</td></tr>
    <tr><td><strong>exacerbate</strong></td><td>make a problem worse</td><td>make worse</td><td>Climate change is likely to exacerbate water scarcity.</td></tr>
    <tr><td><strong>alleviate</strong></td><td>make suffering less severe</td><td>reduce, lessen</td><td>Governments can alleviate poverty through targeted policies.</td></tr>
    <tr><td><strong>encompass</strong></td><td>include or contain as part of a whole</td><td>include, cover</td><td>The curriculum should encompass both academic and vocational subjects.</td></tr>
    <tr><td><strong>underpin</strong></td><td>form the basis or foundation of</td><td>support, be the basis of</td><td>Trust underpins all successful business relationships.</td></tr>
    <tr><td><strong>bolster</strong></td><td>support or strengthen</td><td>help, boost</td><td>Investment in infrastructure can bolster economic growth.</td></tr>
    <tr><td><strong>pervasive</strong></td><td>spreading widely throughout</td><td>widespread, everywhere</td><td>The influence of social media is pervasive in modern society.</td></tr>
    <tr><td><strong>substantiate</strong></td><td>provide evidence to support</td><td>prove, back up</td><td>These claims need to be substantiated with empirical data.</td></tr>
  </tbody>
</table>`,
      points: [
        'Learn each word with its precise meaning — not just as a "fancy synonym"',
        'Note the connotation: "detrimental" implies gradual harm, "devastating" implies sudden destruction',
        '"Exacerbate" and "alleviate" are a natural pair — one worsens, the other lessens',
        '"Underpin" and "bolster" both mean support, but "underpin" = foundation, "bolster" = reinforcement',
      ],
    },
    {
      title: 'Register and Context',
      content: `<p>Nuanced vocabulary must match the <strong>register</strong> (formality level) and <strong>context</strong> (topic and situation) of your writing or speech. Using "exacerbate" in a casual Speaking Part 1 answer about hobbies would sound forced. In a Task 2 essay about environmental degradation, it is perfect.</p>
<h4>Register Guidelines</h4>
<ul>
  <li><strong>Writing Task 2:</strong> Full academic register — use freely</li>
  <li><strong>Writing Task 1:</strong> Semi-formal — use selectively for data commentary</li>
  <li><strong>Speaking Part 3:</strong> Moderately formal — use 2-3 nuanced words naturally</li>
  <li><strong>Speaking Part 1:</strong> Conversational — avoid forcing academic vocabulary</li>
</ul>`,
      points: [
        'Task 2 is the best place to showcase nuanced vocabulary',
        'In Speaking, use nuanced words only when they arise naturally in Part 3',
        'Match the word to the topic: "alleviate" for problems, "bolster" for arguments',
        'If you are unsure about a word, use a simpler alternative correctly instead',
      ],
    },
    {
      title: 'Avoiding Overuse',
      content: `<p>A common mistake among ambitious IELTS candidates is <strong>overloading</strong> every sentence with advanced vocabulary. This makes writing sound unnatural and can obscure meaning — exactly what examiners penalise.</p>
<h4>Overloaded (unnatural)</h4>
<p><em>"The unprecedented and pervasive proliferation of technology has exacerbated the inherently detrimental ramifications of sedentary lifestyles."</em></p>
<h4>Balanced (natural)</h4>
<p><em>"The rapid spread of technology has exacerbated the health problems associated with sedentary lifestyles, which are now pervasive in developed nations."</em></p>`,
      points: [
        'Use 1-2 nuanced words per sentence maximum',
        'Surround advanced words with clear, simple language',
        'Never use a sophisticated word if you are not 100% sure of its meaning',
        'Quality over quantity: one perfectly used word impresses more than five misused ones',
      ],
    },
  ],
  exercises: [
    {
      title: 'Match Nuanced Word to Common Equivalent',
      type: 'select',
      instructions: 'Select the common equivalent that best matches the nuanced word.',
      questions: [
        {
          text: 'What is the common equivalent of "exacerbate"?',
          options: ['improve', 'make worse', 'explain', 'ignore'],
          answer: 'make worse',
          explanation: '"Exacerbate" means to make a problem, situation, or negative feeling worse.',
        },
        {
          text: 'What is the common equivalent of "paramount"?',
          options: ['secondary', 'unrelated', 'most important', 'controversial'],
          answer: 'most important',
          explanation: '"Paramount" means more important than anything else — supreme in rank.',
        },
        {
          text: 'What is the common equivalent of "underpin"?',
          options: ['destroy', 'form the basis of', 'contradict', 'overlook'],
          answer: 'form the basis of',
          explanation: '"Underpin" means to form the foundation or basis for something.',
        },
        {
          text: 'What is the common equivalent of "pervasive"?',
          options: ['rare', 'temporary', 'widespread', 'invisible'],
          answer: 'widespread',
          explanation: '"Pervasive" means spreading widely throughout an area or group — present everywhere.',
        },
      ],
    },
    {
      title: 'Complete with Nuanced Vocabulary',
      type: 'fill-in',
      instructions: 'Fill in the blank with the most appropriate nuanced word from the lesson.',
      questions: [
        {
          text: 'The government introduced new measures to _______ the effects of the economic downturn on low-income families.',
          answer: 'alleviate',
          explanation: '"Alleviate" means to make suffering less severe — perfect for reducing the impact of a crisis.',
        },
        {
          text: 'Access to clean water is _______ to human survival and should never be treated as a luxury.',
          answer: 'paramount',
          explanation: '"Paramount" means of supreme importance — it emphasises that clean water is an absolute priority.',
        },
        {
          text: 'The rise of remote work has been _______ — no industry has been left untouched.',
          answer: 'pervasive',
          explanation: '"Pervasive" means spreading widely throughout, which describes a trend affecting all industries.',
        },
        {
          text: 'Strong evidence is needed to _______ the claim that smaller class sizes improve learning outcomes.',
          answer: 'substantiate',
          explanation: '"Substantiate" means to provide evidence to support or prove a claim.',
        },
      ],
    },
    {
      title: 'Choose the Correct Nuanced Word',
      type: 'select',
      instructions: 'Select the nuanced word that fits the sentence context most precisely.',
      questions: [
        {
          text: '"The discovery of antibiotics was a _______ moment in the history of medicine."',
          options: ['detrimental', 'pivotal', 'pervasive', 'inherent'],
          answer: 'pivotal',
          explanation: '"Pivotal" means of crucial importance as a turning point — the discovery changed everything.',
        },
        {
          text: '"There are _______ challenges in implementing AI in healthcare, regardless of the system used."',
          options: ['unprecedented', 'inherent', 'paramount', 'bolstered'],
          answer: 'inherent',
          explanation: '"Inherent" means existing as a natural part of something — challenges that are built into the very nature of the task.',
        },
        {
          text: '"Lack of sleep can _______ existing mental health conditions."',
          options: ['alleviate', 'encompass', 'exacerbate', 'underpin'],
          answer: 'exacerbate',
          explanation: '"Exacerbate" means to make worse — sleep deprivation worsens existing mental health issues.',
        },
        {
          text: '"The new training programme aims to _______ employees\' confidence and performance."',
          options: ['substantiate', 'bolster', 'exacerbate', 'encompass'],
          answer: 'bolster',
          explanation: '"Bolster" means to support or strengthen — the programme reinforces confidence.',
        },
      ],
    },
    {
      title: 'Write Sentences Using Nuanced Words',
      type: 'writing',
      instructions: 'Write a complete IELTS-style sentence using the given nuanced word correctly.',
      questions: [
        {
          text: 'Write a Task 2 sentence using the word "unprecedented".',
          sampleAnswer: 'The COVID-19 pandemic presented unprecedented challenges to global healthcare systems, exposing longstanding vulnerabilities.',
          explanation: '"Unprecedented" is used correctly to describe something never experienced before — the scale of the pandemic.',
        },
        {
          text: 'Write a Task 2 sentence using the word "encompass".',
          sampleAnswer: 'A well-designed education system should encompass not only academic knowledge but also practical life skills and emotional development.',
          explanation: '"Encompass" correctly describes including multiple elements within a broader whole.',
        },
        {
          text: 'Write a Task 2 sentence using the word "detrimental".',
          sampleAnswer: 'Excessive screen time has been shown to be detrimental to children\'s social development, particularly during their formative years.',
          explanation: '"Detrimental" correctly describes something that causes harm — screen time negatively affecting development.',
        },
      ],
    },
  ],
  tips: [
    'Learn nuanced words in pairs: exacerbate/alleviate, underpin/bolster, pivotal/paramount',
    'Keep a personal vocabulary notebook with word, meaning, common equivalent, and your own example sentence',
    'Read quality journalism (The Economist, BBC, The Guardian) and highlight nuanced vocabulary',
    'Test yourself: can you explain the difference between "pivotal" and "paramount"? If not, review',
    'In your IELTS essay, aim for 4-6 nuanced words total — spread across paragraphs, not clustered',
  ],
  dailyPractice: [
    'Pick two nuanced words and write one IELTS-style sentence for each',
    'Read an editorial article and list every word you could replace with a simpler synonym',
    'Quiz yourself on the twelve high-impact words: cover the meaning column and test recall',
    'Record a 1-minute Speaking Part 3 answer using at least two nuanced words naturally',
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'Nuanced Expressions for Band 8+: Twelve high-impact words (pivotal, inherent, detrimental, paramount, unprecedented, exacerbate, alleviate, encompass, underpin, bolster, pervasive, substantiate) with precise meanings, register-appropriate usage, and strategies to avoid overuse. Difficulty 5 — highest level.',
};

export function getDataForDifficulty(difficulty = 5) {
  if (difficulty <= 2) {
    return {
      ...nuancedExpressionsData,
      title: nuancedExpressionsData.title + ' (Foundations)',
      overview: { ...nuancedExpressionsData.overview, duration: '15-20 minutes' },
      sections: nuancedExpressionsData.sections.slice(0, Math.max(2, Math.ceil(nuancedExpressionsData.sections.length / 2))),
      exercises: nuancedExpressionsData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...nuancedExpressionsData,
      title: nuancedExpressionsData.title + ' (Advanced)',
      overview: { ...nuancedExpressionsData.overview, duration: '30-35 minutes' },
      sections: nuancedExpressionsData.sections,
      exercises: nuancedExpressionsData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(nuancedExpressionsData.tips || []),
        'For Band 8+: Use less common vocabulary naturally in context.',
        'Advanced tip: Focus on collocations and word families, not just individual words.',
      ],
    };
  }
  return nuancedExpressionsData;
}
